import React, { useMemo, useState } from 'react';
import { BsCheckCircle, BsExclamationCircle, BsFillCheckCircleFill } from 'react-icons/bs';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import {
  Box,
  Button,
  Checkbox,
  Divider,
  Flex,
  Grid,
  GridItem,
  HStack,
  Icon,
  Text, Tooltip,
  useToast,
  VStack
} from '@chakra-ui/react';

import { CypressIds } from '../../../constants/cypressIds';
import DataTypes from '../../../constants/dataTypes';
import { CertSubmissionDuskIds } from '../../../constants/duskIds';
import colors from '../../../styles/theme/foundation/colors';
import CertificateCreationBlueprintType from '../../../types/CertificateCreationBlueprintType';
import { getDisplayValueForType } from '../../../utils/tableUtil';

const detailsDataTypes = [
  { displayName: 'Insurance Company', key: 'insurance_company_display_name', dataType: DataTypes.String },
  { displayName: 'Policy Number', key: 'number', dataType: DataTypes.String },
  { displayName: 'Additional Insured', key: 'additional_insured', dataType: DataTypes.Boolean },
  { displayName: 'Subrogation Waived', key: 'subrogation_waived', dataType: DataTypes.Boolean },
  { displayName: 'Policy Effective', key: 'effective_at', dataType: DataTypes.Date },
  { displayName: 'Policy Expires', key: 'expires_at', dataType: DataTypes.Date }
];

const SubmissionStep = ({ blueprint }) => {
  const dispatch = useDispatch();
  const history = useHistory();
  const [liabilityChecked, setLiabilityChecked] = useState(false);
  const [isSubmitting, setSubmitting] = useState(false);
  const requiredDocuments = useSelector(blueprint.selectDocumentRequirements);
  const missingDocTypeIds = useSelector(blueprint.selectMissingDocumentTypeIds);
  const minimumRequirements = useSelector(blueprint.selectMinimumRequirements);
  const policyData = useSelector(blueprint.selectPolicyData);
  const requiredPolicyTypes = useSelector(blueprint.selectRequirementPolicyTypes);
  const selectedAdditionalPolicyTypes = useSelector(blueprint.selectSelectedAdditionalPolicyTypes);
  const submissionText = useSelector(blueprint.selectSubmissionText);
  const options = useSelector(blueprint.selectOptions);

  const providedPolicies = useMemo(() => Object.values(policyData).map(({ properties, details, policyType, skipped }) => {
    if (!skipped) {
      return (
        <VStack alignItems="flex-start" mt={4}>
          <HStack justifyContent="flex-start" key={policyType.id}>
            <Icon as={BsCheckCircle} boxSize={5} color={colors.primaryScale['600']} />
            <Text>{policyType.displayName}</Text>
          </HStack>
          <Grid
            alignItems="center"
            color={colors.greyScale['500']}
            justifyContent="center"
            mt={1}
            templateColumns="repeat(2, 1fr)"
            textAlign="left"
            textStyle="bodySm500"
            w="100%"
          >
            {detailsDataTypes.map(({ key, dataType, displayName }) => (
              <>
                <GridItem textAlign="left" textTransform="capitalize">{displayName}</GridItem>
                <GridItem textAlign="right">{getDisplayValueForType(details[key], dataType)}</GridItem>
              </>
            ))}
          </Grid>

          <Divider borderBottomWidth="3px" />

          <Grid
            alignItems="center"
            color={colors.greyScale['500']}
            justifyContent="center"
            mt={1}
            templateColumns="repeat(2, 1fr)"
            textAlign="left"
            textStyle="bodySm500"
            w="100%"
          >
            {properties.map(({ id, value }) => {
              const propertyType = policyType.properties.find(type => type.id === id);

              if (value && propertyType) {
                return (
                  <>
                    <GridItem textAlign="left" whiteSpace="nowrap">{propertyType.displayName}</GridItem>
                    <GridItem textAlign="right">{getDisplayValueForType(value, propertyType.dataType)}</GridItem>
                  </>
                );
              }

              return null;
            })}
          </Grid>
        </VStack>
      );
    }

    return null;
  }), [minimumRequirements, policyData, requiredPolicyTypes]);

  const outstandingPolicies = useMemo(() => Object.values(minimumRequirements).map(policy => {
    if (!policyData[policy.policyTypeId] || policyData[policy.policyTypeId].skipped) {
      return (
        <HStack color={colors.greyScale['500']} justifyContent="flex-start" key={policy.policyTypeId}>
          <Icon as={BsExclamationCircle} boxSize={6} />
          <Text>{policy.policyTypeDisplayName}</Text>
        </HStack>
      );
    }

    return null;
  }), [minimumRequirements, policyData]);

  const providedDocuments = useMemo(() => requiredDocuments.map(doc => {
    if (!missingDocTypeIds.includes(doc.insuranceDocumentTypeId)) {
      return (
        <HStack key={doc.insuranceDocumentTypeId} mb={3}>
          <Icon as={BsFillCheckCircleFill} boxSize={8} color={colors.primaryScale['600']} />
          <Text>{doc.insuranceDocumentTypeDisplayName}</Text>
        </HStack>
      );
    }

    return null;
  }), [missingDocTypeIds, requiredDocuments]);

  const outstandingDocuments = useMemo(() => requiredDocuments.map(doc => {
    if (missingDocTypeIds.includes(doc.insuranceDocumentTypeId)) {
      return (
        <HStack color={colors.greyScale['500']} key={doc.insuranceDocumentTypeId} mb={3}>
          <Icon as={BsExclamationCircle} boxSize={5} />
          <Text>{doc.insuranceDocumentTypeDisplayName}</Text>
        </HStack>
      );
    }

    return null;
  }), [missingDocTypeIds, requiredDocuments]);

  const onSubmit = () => {
    setSubmitting(true);

    const onSuccess = () => {
      (options.afterSubmitRedirectUrl ? history.push(options.afterSubmitRedirectUrl) : null);
      setSubmitting(false);
    };

    const onFail = () => setSubmitting(false);

    dispatch(blueprint.submit(onSuccess, onFail));
  };

  return (
    <Flex backgroundColor="white" flexDir="column" height="100%" overflow="auto" p={8} w="100%">
      <Box flexBasis="100%" justifyContent="center" mb={8} textAlign="center">
        <Text color="greyScale.900" mb={4} textStyle="displayXs">
          Ready to Submit?
        </Text>
        <Text
          color="greyScale.500"
          maxW="700px"
          mb={6}
          mx="auto"
          textStyle="bodyMd500"
        >
          {submissionText}
        </Text>
      </Box>
      <Flex direction="column">
        <HStack alignItems="flex-start" mb="4rem" spacing={0}>
          <Box minW="500px" width="55%">
            <Text mb={4} ml={4} textStyle="bodyXxl600">Provided Policies</Text>
            <Box
              borderRight={`3px solid ${colors.greyScale['200']}`}
              borderTop={`3px solid ${colors.greyScale['200']}`}
              maxHeight="30vh"
              minHeight="15vh"
              overflowY="auto"
              p={5}
            >
              {providedPolicies?.length
                ? providedPolicies
                : (<Text fontStyle="italic">No provided policies.</Text>)}
            </Box>
          </Box>
          <Box minW="300px" width="45%">
            <Text mb={4} ml={4} textStyle="bodyXxl600">Outstanding Policies</Text>
            <Box
              borderTop={`3px solid ${colors.greyScale['200']}`}
              maxHeight="30vh"
              minHeight="15vh"
              overflowY="auto"
              p={5}
            >
              {outstandingPolicies?.length
                ? outstandingPolicies
                : (<Text fontStyle="italic">No outstanding policies.</Text>)}
            </Box>
          </Box>
        </HStack>
        <HStack alignItems="flex-start" mb="2rem" spacing={0}>
          <Box minW="500px" width="55%">
            <Text mb={4} ml={4} textStyle="bodyXxl600">Provided Documents</Text>
            <Box
              borderRight={`3px solid ${colors.greyScale['200']}`}
              borderTop={`3px solid ${colors.greyScale['200']}`}
              maxHeight="30vh"
              minHeight="15vh"
              overflowY="auto"
              p={5}
            >
              {providedDocuments?.length
                ? providedDocuments
                : <Text fontStyle="italic">No provided documents.</Text>}
            </Box>
          </Box>
          <Box minW="300px" width="45%">
            <Text mb={4} ml={4} textStyle="bodyXxl600">Outstanding Documents</Text>
            <Box
              borderTop={`3px solid ${colors.greyScale['200']}`}
              maxHeight="30vh"
              minHeight="15vh"
              overflowY="auto"
              p={5}
            >
              {outstandingDocuments?.length
                ? outstandingDocuments
                : <Text fontStyle="italic">No outstanding documents.</Text>}
            </Box>
          </Box>
        </HStack>
      </Flex>

      <HStack alignItems="flex-start" justifyContent="center" mb={4} width="100%">
        <Checkbox
          borderColor="greyScale.700"
          data-cy={CypressIds.certSubmissionConfirmLanguageCheckbox}
          dusk={CertSubmissionDuskIds.confirmationLanguageCheckbox}
          id="cert-submit-acknowledge-checkbox"
          isChecked={liabilityChecked}
          size="lg"
          onChange={() => setLiabilityChecked(prev => !prev)}
        />
        <Text
          cursor="pointer"
          width="50%"
          onClick={() => {
            const acknowledgeCheckBox = document.getElementById('cert-submit-acknowledge-checkbox');

            acknowledgeCheckBox.dispatchEvent(new MouseEvent('click', { bubbles: true }));
          }}
        >
          I have reviewed my submission and I understand that I am responsible for the information submitted into PINS.
          PINS Advantage assumes no responsibility for any errors or omissions in the contents of this service.
        </Text>
      </HStack>

      <HStack flexBasis="100%" justifyContent="center" mt="auto" mx="auto" spacing={3}>
        <Button
          dusk={CertSubmissionDuskIds.backConfirmationButton}
          variant="secondary"
          onClick={() => {
            let count = 2;

            if (!requiredPolicyTypes.length && !selectedAdditionalPolicyTypes.length) {
              count = 3;
            }

            dispatch(blueprint.goBack(count));
          }}
        >
          Back
        </Button>
        <Box>
          <Tooltip
            isDisabled={liabilityChecked}
            label="To submit, please check the box above acknowledging the terms and conditions."
          >
            <Box>
              <Button
                data-cy={CypressIds.certSubmissionSubmitCertificateButton}
                dusk={CertSubmissionDuskIds.submitCertificateButton}
                isDisabled={isSubmitting || !liabilityChecked}
                variant="gradientSuccess"
                onClick={onSubmit}
              >
                Submit Insurance
              </Button>
            </Box>
          </Tooltip>
        </Box>
      </HStack>
    </Flex>
  );
};

SubmissionStep.propTypes = { blueprint: CertificateCreationBlueprintType };

export default SubmissionStep;
